/**
 * Announcer voice-over (VO) system for Counter Pump.
 *
 * Uses the Web Speech API (SpeechSynthesisUtterance) for text-to-speech
 * announcements of round events, bomb status, and kill streaks.
 * Falls back gracefully when SpeechSynthesis is unavailable.
 *
 * Features:
 * - Queue system: announcements never overlap; they play sequentially
 * - Volume control via a dedicated announcerVolume setting
 * - Speech rate 0.9 / pitch 0.8 for an authoritative tone
 * - Enable/disable toggle
 */

// ─── Announcer Events ──────────────────────────────────────────────────────

export enum AnnouncerEvent {
  CTWin,
  TWin,
  BombPlanted,
  BombDefused,
  Countdown,
  FirstBlood,
  Ace,
  MatchPoint,
  HalfTime,
  OvertimeStart,
  // ── Slice C AUD-I2 expansion ──
  Countdown_1min,
  Countdown_30sec,
  Site_Taken,
  Site_Lost,
  Clutch,
  EcoRound,
}

/** Maps each announcer event to its spoken text. */
export const ANNOUNCER_EVENT_TEXT: Record<AnnouncerEvent, string> = {
  [AnnouncerEvent.CTWin]: 'Counter-Terrorists Win',
  [AnnouncerEvent.TWin]: 'Terrorists Win',
  [AnnouncerEvent.BombPlanted]: 'Bomb has been planted',
  [AnnouncerEvent.BombDefused]: 'Bomb has been defused',
  [AnnouncerEvent.Countdown]: 'Round starting in 3... 2... 1...',
  [AnnouncerEvent.FirstBlood]: 'First blood',
  [AnnouncerEvent.Ace]: 'Ace!',
  [AnnouncerEvent.MatchPoint]: 'Match point',
  [AnnouncerEvent.HalfTime]: 'Switching sides',
  [AnnouncerEvent.OvertimeStart]: 'Overtime',
  [AnnouncerEvent.Countdown_1min]: 'One minute remaining',
  [AnnouncerEvent.Countdown_30sec]: 'Thirty seconds remaining',
  [AnnouncerEvent.Site_Taken]: 'Site captured',
  [AnnouncerEvent.Site_Lost]: 'Site lost',
  [AnnouncerEvent.Clutch]: 'Clutch situation',
  [AnnouncerEvent.EcoRound]: 'Eco round',
};

// ─── Speech config constants ───────────────────────────────────────────────

const SPEECH_RATE = 0.9;
const SPEECH_PITCH = 0.8;

// ─── AnnouncerSystem ───────────────────────────────────────────────────────

export class AnnouncerSystem {
  /** Whether the Web Speech API is available in this environment. */
  readonly hasSpeechSynthesis: boolean;

  private volume = 1.0;
  private enabled = true;
  private disposed = false;

  /** Internal queue of pending announcement texts. */
  private queue: string[] = [];
  /** Whether we are currently speaking an utterance. */
  private speaking = false;
  /**
   * Retained reference to the utterance currently being spoken. Chrome can
   * GC the SpeechSynthesisUtterance before `onend` fires if nothing holds
   * a reference, which permanently wedges `speaking = true` and blocks the
   * queue. Holding the ref here prevents that.
   */
  private currentUtterance: SpeechSynthesisUtterance | null = null;

  /**
   * Optional ducking hook fired whenever speech starts. Game wires this to
   * `musicSystem.duckForAnnouncer()` so the score/menu music drops while VO
   * plays. Kept as an injection point (instead of importing music.ts) so tests
   * don't need to instantiate AudioContext.
   */
  private onSpeakStart: (() => void) | null = null;
  /** Counterpart of onSpeakStart, fired on utterance end/error. */
  private onSpeakEnd: (() => void) | null = null;

  constructor() {
    this.hasSpeechSynthesis =
      typeof globalThis.speechSynthesis !== 'undefined' &&
      typeof globalThis.SpeechSynthesisUtterance !== 'undefined';
  }

  // ── Public API ─────────────────────────────────────────────────────────

  /**
   * Queue an announcement for the given event.
   * If nothing is currently playing, it starts immediately.
   * If another announcement is in progress, this one is queued.
   */
  announce(event: AnnouncerEvent): void {
    if (this.disposed || !this.enabled) return;

    const text = ANNOUNCER_EVENT_TEXT[event];
    if (!text) return;

    if (!this.hasSpeechSynthesis) {
      // Fallback: silently ignore — no TTS available
      return;
    }

    if (this.speaking) {
      // Queue it for later
      this.queue.push(text);
      return;
    }

    this.speakText(text);
  }

  /** Set the announcer volume (clamped to 0-1). */
  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
  }

  /** Enable or disable announcements. */
  setEnabled(value: boolean): void {
    this.enabled = value;
    if (!value && this.hasSpeechSynthesis) {
      this.cancelAll();
    }
  }

  /** Tear down the announcer, cancelling any in-progress speech. */
  dispose(): void {
    this.disposed = true;
    this.cancelAll();
  }

  /**
   * Register hooks fired when speech starts / ends (or errors). Used by the
   * game to duck music while the announcer is talking.
   */
  setSpeechHooks(hooks: { onStart?: () => void; onEnd?: () => void }): void {
    this.onSpeakStart = hooks.onStart ?? null;
    this.onSpeakEnd = hooks.onEnd ?? null;
  }

  // ── Private ────────────────────────────────────────────────────────────

  private speakText(text: string): void {
    if (!this.hasSpeechSynthesis) return;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = SPEECH_RATE;
    utterance.pitch = SPEECH_PITCH;
    utterance.volume = this.volume;

    const done = () => {
      if (this.currentUtterance === utterance) {
        this.currentUtterance = null;
      }
      this.speaking = false;
      // Restore music volume only when the queue is fully drained, so back-to-back
      // announcements don't bounce the music level.
      if (this.queue.length === 0) {
        try { this.onSpeakEnd?.(); } catch { /* hook errors must not break the queue */ }
      }
      this.processQueue();
    };

    utterance.onend = done;
    utterance.onerror = done;

    // Retain a reference so Chrome doesn't GC the utterance before onend fires.
    this.currentUtterance = utterance;
    this.speaking = true;
    try { this.onSpeakStart?.(); } catch { /* hook errors must not block speech */ }
    speechSynthesis.speak(utterance);
  }

  private processQueue(): void {
    if (this.disposed || this.queue.length === 0) return;

    const next = this.queue.shift()!;
    this.speakText(next);
  }

  private cancelAll(): void {
    const wasSpeaking = this.speaking;
    this.queue.length = 0;
    this.speaking = false;
    this.currentUtterance = null;
    if (this.hasSpeechSynthesis && typeof globalThis.speechSynthesis !== 'undefined') {
      speechSynthesis.cancel();
    }
    if (wasSpeaking) {
      try { this.onSpeakEnd?.(); } catch { /* hook errors must not crash teardown */ }
    }
  }
}

/**
 * Singleton announcer instance.
 *
 * A-L-001: Eager construction is intentional and differs from `musicSystem` /
 * `ambientAudio` (which use Proxy-lazy wrappers). The AnnouncerSystem
 * constructor is side-effect-free — only `typeof globalThis.speechSynthesis`
 * feature checks — so it is cheap and safe to instantiate at module load.
 * The lazy-Proxy pattern is reserved for audio systems that allocate an
 * AudioContext in their constructor (browser autoplay gate).
 */
export const announcer = new AnnouncerSystem();

